import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';

interface FormSelectProps {
  label?: string;
  /**
   * Forwarded to the native `<select>` so a caller that draws its own label can
   * point `<label htmlFor>` at it. Without it the control has no accessible
   * name.
   */
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  name?: string;
  fullWidth?: boolean;
  placeholder?: string;
  defaultValue?: string;
  error?: boolean;
  greyBackground?: boolean;
  autoComplete?: string;
}

export function FormSelect({
  label,
  id,
  value,
  onChange,
  options,
  name,
  fullWidth = false,
  placeholder,
  defaultValue,
  error = false,
  greyBackground = false,
  autoComplete,
}: FormSelectProps) {
  const currentValue = value || defaultValue || '';

  const selectedOption = options.find(
    (option) => option.value === currentValue,
  );
  const displayText = selectedOption?.label || placeholder || currentValue;

  return (
    <SelectGroup $fullWidth={fullWidth}>
      {label && <SelectLabel>{label}</SelectLabel>}
      <SelectWrapper>
        <Select
          id={id}
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          name={name}
          autoComplete={autoComplete}
        >
          {placeholder && (
            <option value="" disabled={!value && !defaultValue}>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <SelectDisplay $error={error} $greyBackground={greyBackground}>
          <SelectText>{displayText || '\u00A0'}</SelectText>
          <SelectIcon>
            <FontAwesomeIcon icon={faCaretDown} />
          </SelectIcon>
        </SelectDisplay>
      </SelectWrapper>
    </SelectGroup>
  );
}

const SelectGroup = styled.div<{ $fullWidth: boolean }>`
  align-items: center;
  display: ${(props) => (props.$fullWidth ? 'block' : 'grid')};
  gap: 8px;
  grid-template-columns: ${(props) => (props.$fullWidth ? 'none' : 'auto 1fr')};
  position: relative;
`;

const SelectLabel = styled.div`
  color: var(--grey-700);
  font-family: var(--raleway);
  font-size: 12px;
  font-weight: 300;
  margin-bottom: 0;
  text-align: right;
`;

const SelectWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`;

const Select = styled.select`
  appearance: none;
  background: transparent;
  border: none;
  color: transparent;
  cursor: pointer;
  font-family: var(--raleway);
  font-size: 12px;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 2;

  &:focus {
    outline: none;
    & + div {
      background-color: var(--grey-100);
      border-color: var(--black);
    }
  }
`;

const SelectDisplay = styled.div<{
  $error?: boolean;
  $greyBackground?: boolean;
}>`
  background: ${({ $greyBackground }) =>
    $greyBackground ? 'var(--grey-75)' : 'var(--white)'};
  border: 1px solid
    ${({ $error }) => ($error ? 'var(--wbs-red)' : 'var(--grey-300)')};
  border-radius: 4px;
  box-shadow: var(--shadow-inset-sm);
  display: flex;
  pointer-events: none;
  width: 100%;
  z-index: 1;
`;

const SelectText = styled.div`
  color: var(--grey-900);
  flex: 1;
  font-family: var(--raleway);
  font-size: 12px;
  letter-spacing: 0.5px;
  min-width: 0;
  overflow: hidden;
  padding: 7px 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectIcon = styled.div`
  align-items: center;
  color: var(--grey-600);
  display: flex;
  font-size: 16px;
  padding: 0 5px;
`;
